import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Card } from "../components/ui";
import { api, type OrderBatch, type OrderBatchStatus } from "../data/api";
import { parseCsv, type ParseError } from "../features/programacion/csvParser";
import { saleRowsToCsvRows } from "../features/programacion/orderBatchMapping";
import type { SaleRow } from "../features/programacion/types";

// ProgramacionPage — landing del módulo. Lista los batches de órdenes
// (lotes subidos hoy o en fechas anteriores) y permite:
//   - Subir un CSV → crea un batch nuevo con sus órdenes persistidas
//   - Filtrar por fecha / estado / nombre
//   - Seleccionar uno o más batches y "Comenzar planificación", que
//     marca su estado como 'planning' y navega al optimizador.
//
// La pantalla del optimizador en sí vive en /programacion/planificar
// (ProgramacionPlanPage). Hasta Fase 3 esa página sigue parseando el
// CSV en memoria; recién después leerá las órdenes ya persistidas
// desde los batches seleccionados.

type StatusFilter = OrderBatchStatus | "all";

const STATUS_VARIANTS: Record<OrderBatchStatus, { variant: Parameters<typeof Badge>[0]["variant"]; label: string }> = {
  created:  { variant: "pending",   label: "Por planificar" },
  planning: { variant: "in-route",  label: "Planificando" },
  planned:  { variant: "delivered", label: "Planificado" },
};

export function ProgramacionPage() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<OrderBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros UI
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState<string>(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState<string>("");

  // Selección para "Comenzar planificación"
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listOrderBatches({
        status: statusFilter === "all" ? undefined : statusFilter,
        search: search.trim() || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setBatches(res);
      // Limpiar selección de batches que ya no están en el filtro.
      setSelected((prev) => {
        const ids = new Set(res.map((b) => b.id));
        const next = new Set<string>();
        for (const id of prev) if (ids.has(id)) next.add(id);
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, fromDate, toDate]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectableIds = useMemo(
    () => batches.filter((b) => b.status !== "planned").map((b) => b.id),
    [batches],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };

  // "Comenzar planificación": marca los batches en 'planning' y navega
  // al optimizador. La página de planificación lee los IDs desde state.
  const onStartPlanning = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map((id) => api.updateOrderBatchStatus(id, "planning")));
      navigate("/programacion/planificar", { state: { batchIds: ids } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [selected, navigate]);

  return (
    <div className="space-y-4 p-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Programación</h1>
          <p className="text-sm text-sub">
            Selecciona uno o más lotes de órdenes para planificar las rutas del día.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowUpload(true)}>
          Subir CSV
        </Button>
      </header>

      {/* Filtros */}
      <Card padding="none">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3">
          <label className="text-sm">
            <span className="block text-sub mb-1">Buscar por nombre</span>
            <input
              type="text"
              className="w-full border border-border rounded px-2 py-1.5"
              placeholder="ej. BATCH-09:15"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="block text-sub mb-1">Estado</span>
            <select
              className="w-full border border-border rounded px-2 py-1.5"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">Todos</option>
              <option value="created">Por planificar</option>
              <option value="planning">Planificando</option>
              <option value="planned">Planificado</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-sub mb-1">Desde</span>
            <input
              type="date"
              className="w-full border border-border rounded px-2 py-1.5"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="block text-sub mb-1">Hasta</span>
            <input
              type="date"
              className="w-full border border-border rounded px-2 py-1.5"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
        </div>
      </Card>

      {/* Tabla de batches */}
      <Card padding="none">
        <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-border">
          <div className="text-sm text-sub">
            {loading ? "Cargando…" : `${batches.length} lote${batches.length === 1 ? "" : "s"}`}
            {selected.size > 0 && (
              <span className="ml-3 text-ink">• {selected.size} seleccionado{selected.size === 1 ? "" : "s"}</span>
            )}
          </div>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={onStartPlanning}
          >
            Comenzar planificación{selected.size > 0 ? ` (${totalSelectedOrders(batches, selected)})` : ""}
          </Button>
        </div>

        {error && (
          <div className="px-3 py-2 text-sm text-state-failed bg-state-failed/10 border-b border-state-failed/30">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-sub">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={selectableIds.length === 0}
                  />
                </th>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Origen</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Órdenes</th>
                <th className="px-3 py-2">Creado</th>
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sub">
                    No hay lotes que coincidan. Sube un CSV para empezar.
                  </td>
                </tr>
              )}
              {batches.map((b) => {
                const meta = STATUS_VARIANTS[b.status];
                const isPlanned = b.status === "planned";
                return (
                  <tr
                    key={b.id}
                    className={`border-t border-border ${isPlanned ? "opacity-60" : "hover:bg-page cursor-pointer"}`}
                    onClick={() => !isPlanned && toggleOne(b.id)}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(b.id)}
                        onChange={() => toggleOne(b.id)}
                        disabled={isPlanned}
                        aria-label={`Seleccionar ${b.name}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={`/programacion/batches/${b.id}`}
                        className="text-primary hover:text-primary-hover hover:underline"
                      >
                        {b.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 uppercase text-sub">{b.source}</td>
                    <td className="px-3 py-2">
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{b.num_orders}</td>
                    <td className="px-3 py-2 text-sub">{formatDateTime(b.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {showUpload && (
        <UploadCsvModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            void reload();
          }}
        />
      )}
    </div>
  );
}

// ---------------- Helpers ----------------

function totalSelectedOrders(batches: OrderBatch[], selected: Set<string>): number {
  let total = 0;
  for (const b of batches) if (selected.has(b.id)) total += b.num_orders;
  return total;
}

function formatDateTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------- Upload modal ----------------

function UploadCsvModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: (batch: OrderBatch) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SaleRow[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const onSelectFile = useCallback(async (f: File) => {
    setFile(f);
    setParsing(true);
    setError(null);
    setPreview(null);
    setWarnings([]);
    const r = await parseCsv(f);
    setParsing(false);
    if (!r.ok) {
      setError(describeParseError(r.error));
      return;
    }
    setPreview(r.data.rows);
    setWarnings(r.data.warnings);
  }, []);

  const onUpload = useCallback(async () => {
    if (!preview || preview.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const res = await api.uploadOrderBatchCsv({
        name: name.trim() || undefined,
        rows: saleRowsToCsvRows(preview),
      });
      onUploaded(res.batch);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }, [preview, name, onUploaded]);

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Subir CSV de órdenes</h2>
          <button
            className="text-sub hover:text-ink text-xl leading-none"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-sub mb-1">Nombre del lote (opcional)</span>
            <input
              type="text"
              className="w-full border border-border rounded px-2 py-1.5"
              placeholder="Se autogenera (ej. BATCH-09:15)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="block text-sm">
            <span className="block text-sub mb-1">Archivo CSV</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onSelectFile(f);
              }}
            />
            {file && <div className="mt-1 text-xs text-sub">{file.name}</div>}
          </label>

          {parsing && <div className="text-sm text-sub">Procesando CSV…</div>}

          {preview && (
            <div className="text-sm">
              <div className="font-medium text-ink">
                {preview.length} fila{preview.length === 1 ? "" : "s"} válida{preview.length === 1 ? "" : "s"}
              </div>
              {warnings.length > 0 && (
                <details className="mt-1 text-state-delayed">
                  <summary className="cursor-pointer">
                    {warnings.length} advertencia{warnings.length === 1 ? "" : "s"}
                  </summary>
                  <ul className="list-disc pl-5 mt-1 text-xs">
                    {warnings.slice(0, 10).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                    {warnings.length > 10 && <li>… y {warnings.length - 10} más</li>}
                  </ul>
                </details>
              )}
            </div>
          )}

          {error && (
            <div className="text-sm text-state-failed bg-state-failed/10 border border-state-failed/30 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onUpload}
            disabled={!preview || preview.length === 0 || uploading}
          >
            {uploading ? "Subiendo…" : "Crear lote"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function describeParseError(err: ParseError): string {
  switch (err.kind) {
    case "missing-headers":
      return `Faltan columnas en el CSV: ${err.missing.join(", ")}`;
    case "no-data":
      return "El CSV no contiene filas con datos.";
    case "papaparse":
      return `Error parseando CSV: ${err.message}`;
  }
}
